import React, { createContext, ReactNode, useContext, useState, useEffect } from 'react';
import { ActiveFilter } from '../../ideas/filters/FiltersMenu';
import SingleCustomerContext from '../Customer-Context/SingleCustomerContext';

interface CustomersEvidenceFiltersContextData {
    openedMenuType: "idea" | "evidence" | null;
    setOpenedMenuType: React.Dispatch<React.SetStateAction<"idea" | "evidence" | null>>;
    activeFilters: ActiveFilter[];
    setActiveFilters: React.Dispatch<React.SetStateAction<ActiveFilter[]>>;
    selectedParentFilter: string | null;
    setSelectedParentFilter: React.Dispatch<React.SetStateAction<string | null>>;
    checkedComponents: { [key: string]: any[] };
    setCheckedComponents: React.Dispatch<React.SetStateAction<{[key: string]: any[];}>>;
    childFiltersValue: { [key: string]: any[] };
    setChildFiltersValue: React.Dispatch<React.SetStateAction<{ [key: string]: any[] }>>;
    parentQuery: string;
    setParentQuery: React.Dispatch<React.SetStateAction<string>>;
    childQuery: { [x: string]: string } | undefined;
    setChildQuery: React.Dispatch<React.SetStateAction<{ [x: string]: string } | undefined>>;
    query: string | undefined;
    setQuery: React.Dispatch<React.SetStateAction<string | undefined>>;
    filteredComponents: any[];
    setFilteredComponents: React.Dispatch<React.SetStateAction<any[]>>;
    operator: { [x: string]: string } | undefined;
    setOperator: React.Dispatch<React.SetStateAction<{ [x: string]: string } | undefined>>;
    numberValue: { [parent: string]: number } | undefined;
    setNumberValue: React.Dispatch<React.SetStateAction<{ [parent: string]: number } | undefined>>;
    rangeDates: { [parent: string]: [Date | null, Date | null] } | undefined;
    setRangeDates: React.Dispatch<React.SetStateAction<{ [parent: string]: [Date | null, Date | null] } | undefined>>;
    searchedOptions: {[x: string]: string[];} | null;
    setSearchedOptions: React.Dispatch<React.SetStateAction<{[x: string]: string[];} | null>>;
    subPageName: string | undefined;
    setSubPageName: React.Dispatch<React.SetStateAction<string | undefined>>;
    subPageKey: string | undefined;
    setSubPageKey: React.Dispatch<React.SetStateAction<string | undefined>>;
    editMode: boolean;
    setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
}

interface CustomersEvidenceFiltersContextProviderProps {
  children: ReactNode;
}

const CustomersEvidenceFiltersContext = createContext<CustomersEvidenceFiltersContextData>({} as CustomersEvidenceFiltersContextData);

export const CustomersEvidenceFiltersContextProvider = ({ children }: CustomersEvidenceFiltersContextProviderProps) => {
  const { evidenceFilters, setEvidenceFilters, threeSixtyConfig } = useContext(SingleCustomerContext);

  // Get global filters from threeSixtyConfig
  const globalFilters = threeSixtyConfig?.globalFilters || [];

  // Use evidenceFilters from SingleCustomerContext as the activeFilters
  const activeFilters = evidenceFilters || [];
  
  // Wrapper for setActiveFilters that updates evidenceFilters in SingleCustomerContext
  // Always merges new filters with global filters instead of overwriting
  const setActiveFilters = React.useCallback<React.Dispatch<React.SetStateAction<ActiveFilter[]>>>((filters) => {
    if (typeof filters === 'function') {
      setEvidenceFilters((prev: ActiveFilter[]) => {
        // Separate global filters from user filters in prev
        const prevUserFilters = prev.filter((f: ActiveFilter) => 
          !globalFilters.some((gf: ActiveFilter) => gf.parent === f.parent)
        );
        // Apply the update function to user filters only
        const updatedUserFilters = filters(prevUserFilters);
        // Merge: update global filters if user modified them, otherwise keep original global filters
        const updatedGlobalFilters = globalFilters.map((gf: ActiveFilter) => {
          const matchingUserFilter = updatedUserFilters.find((uf: ActiveFilter) => uf.parent === gf.parent);
          return matchingUserFilter || gf;
        });
        // Get user filters that don't match any global filter
        const newUserFilters = updatedUserFilters.filter((uf: ActiveFilter) => 
          !globalFilters.some((gf: ActiveFilter) => gf.parent === uf.parent)
        );
        return [...updatedGlobalFilters, ...newUserFilters];
      });
    } else {
      // Merge: update global filters if user modified them, otherwise keep original global filters
      const updatedGlobalFilters = globalFilters.map((gf: ActiveFilter) => {
        const matchingUserFilter = filters.find((uf: ActiveFilter) => uf.parent === gf.parent);
        return matchingUserFilter || gf;
      });
      // Get user filters that don't match any global filter
      const newUserFilters = filters.filter((uf: ActiveFilter) => 
        !globalFilters.some((gf: ActiveFilter) => gf.parent === uf.parent)
      );
      setEvidenceFilters([...updatedGlobalFilters, ...newUserFilters]);
    }
  }, [setEvidenceFilters, globalFilters]);

  const [openedMenuType, setOpenedMenuType] = useState<"idea" | "evidence" | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectedParentFilter, setSelectedParentFilter] = useState<string | null>(null);
  const [checkedComponents, setCheckedComponents] = useState<{ [key: string]: any[] }>({});
  const [childFiltersValue, setChildFiltersValue] = useState<{ [key: string]: any[] }>({});
  const [parentQuery, setParentQuery] = useState<string>("");
  const [childQuery, setChildQuery] = useState<{ [x: string]: string } | undefined>(undefined);
  const [query, setQuery] = useState<string | undefined>(undefined);
  const [filteredComponents, setFilteredComponents] = useState<any[]>([]);
  const [operator, setOperator] = useState<{ [x: string]: string } | undefined>(undefined);
  const [numberValue, setNumberValue] = useState<{ [parent: string]: number } | undefined>(undefined);
  const [rangeDates, setRangeDates] = useState<{ [parent: string]: [Date | null, Date | null] } | undefined>(undefined);
  const [searchedOptions, setSearchedOptions] = useState<{[x: string]: string[];} | null>(null);
  const [subPageName, setSubPageName] = useState<string | undefined>(undefined);
  const [subPageKey, setSubPageKey] = useState<string | undefined>(undefined);

  return (
    <CustomersEvidenceFiltersContext.Provider value={{
      openedMenuType,
      setOpenedMenuType,
      activeFilters,
      setActiveFilters,
      selectedParentFilter,
      setSelectedParentFilter,
      checkedComponents,
      setCheckedComponents,
      childFiltersValue,
      setChildFiltersValue,
      parentQuery,
      setParentQuery,
      childQuery,
      setChildQuery,
      query,
      setQuery,
      filteredComponents,
      setFilteredComponents,
      operator,
      setOperator,
      numberValue,
      setNumberValue,
      rangeDates,
      setRangeDates,
      searchedOptions,
      setSearchedOptions,
      subPageName,
      setSubPageName,
      subPageKey,
      setSubPageKey,
      editMode,
      setEditMode
    }}>
      {children}
    </CustomersEvidenceFiltersContext.Provider>
  );
};

export default CustomersEvidenceFiltersContext;

